import React, { useState, KeyboardEvent } from "react";
import { Link } from "react-router-dom";
import useRestaurantData from "../hooks/useRestaurantData";
import useOnline from "../hooks/useOnline";
import RestaurantCard from "./RestaurantCard";
import Shimmer from "./Shimmer";
import UserOffline from "./UserOffline";
import { RestaurantCard as RestaurantCardType, FoodCategory } from "../types";
import { IMG_CDN_URL } from "../constants";

const ITEMS_PER_PAGE = 8;

const Body: React.FC = () => {
    const [searchText, setSearchText] = useState<string>("");
    const [searchResults, setSearchResults] = useState<RestaurantCardType[] | null>(null);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

    const {
        allRestaurants,
        filteredRestaurants,
        topBrands,
        foodCategories,
        loading,
        cityName
    } = useRestaurantData();
    const isOnline = useOnline();

    if (!isOnline) {
        return <UserOffline />;
    }

    const filterRestaurants = (text: string, restaurants: RestaurantCardType[] | null) => {
        if (!restaurants) return;

        if (text !== "") {
            const filtered = restaurants.filter((restaurant) =>
                restaurant?.info?.name.toLowerCase().includes(text.toLowerCase()) ||
                (typeof restaurant?.info?.cuisines === 'string' &&
                    restaurant.info.cuisines.toLowerCase().includes(text.toLowerCase())) ||
                (Array.isArray(restaurant?.info?.cuisines) &&
                    restaurant.info.cuisines.some(c => c.toLowerCase().includes(text.toLowerCase())))
            );
            setSearchResults(filtered);
            setErrorMessage(filtered.length === 0 ? "No restaurants found" : "");
            setCurrentPage(1);
        } else {
            setSearchResults(null);
            setErrorMessage("");
            setCurrentPage(1);
        }
    };

    // Filter by food category
    const handleCategoryClick = (categoryText: string) => {
        if (!allRestaurants) return;

        if (selectedCategory === categoryText) {
            // Deselect category
            setSelectedCategory(null);
            setSearchResults(null);
            setErrorMessage("");
            setCurrentPage(1);
        } else {
            // Filter by category
            setSelectedCategory(categoryText);
            const filtered = allRestaurants.filter((restaurant: RestaurantCardType) => {
                const cuisines = restaurant?.info?.cuisines;
                if (typeof cuisines === 'string') {
                    return cuisines.toLowerCase().includes(categoryText.toLowerCase());
                }
                if (Array.isArray(cuisines)) {
                    return cuisines.some(c => c.toLowerCase().includes(categoryText.toLowerCase()));
                }
                return false;
            });
            setSearchResults(filtered);
            setErrorMessage(filtered.length === 0 ? `No restaurants found for ${categoryText}` : "");
            setCurrentPage(1);
        }
    };

    const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            filterRestaurants(searchText, allRestaurants);
        }
    };

    if (loading) {
        return (
            <main className="pt-16 min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
                <section className="relative py-20 px-6 max-w-[1400px] mx-auto flex flex-col items-center justify-center min-h-[40vh] text-center">
                    {/* Animated Icons */}
                    <div className="flex items-center gap-6 mb-8 text-orange-500/80 dark:text-orange-400/80">
                        <i className="fa-solid fa-burger text-4xl animate-bounce" style={{ animationDelay: '0s' }}></i>
                        <i className="fa-solid fa-pizza-slice text-4xl animate-bounce" style={{ animationDelay: '0.2s' }}></i>
                        <i className="fa-solid fa-utensils text-4xl animate-bounce" style={{ animationDelay: '0.4s' }}></i>
                    </div>

                    <h1 className="text-3xl md:text-5xl font-extrabold text-gray-800 dark:text-gray-100 mb-4 animate-pulse">
                        Looking for great food...
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 text-lg max-w-lg">
                        We are curating the best restaurants around you. Hang tight!
                    </p>
                </section>
                <Shimmer />
            </main>
        );
    }

    const displayRestaurants = searchResults ?? filteredRestaurants;

    // Pagination logic
    const totalItems = displayRestaurants?.length || 0;
    const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const currentRestaurants = displayRestaurants?.slice(startIndex, endIndex) || [];

    const goToPage = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            window.scrollTo({ top: 500, behavior: 'smooth' });
        }
    };

    const getPageNumbers = (): (number | string)[] => {
        const pages: (number | string)[] = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (currentPage <= 4) {
                pages.push(1, 2, 3, 4, 5, '...', totalPages);
            } else if (currentPage >= totalPages - 3) {
                pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
            } else {
                pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
            }
        }
        return pages;
    };

    return (
        <main className="pt-16 min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
            {/* Hero Section */}
            <section className="relative py-24 px-6 md:px-12 bg-orange-50/50 dark:bg-gray-900 transition-colors duration-200 overflow-hidden min-h-[600px] flex items-center justify-center">
                {/* Background Decoration - Richer Gradients */}
                <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
                    <div className="absolute -top-[30%] -left-[10%] w-[70%] h-[70%] rounded-full bg-gradient-to-r from-orange-200/40 to-amber-200/40 dark:from-orange-900/20 dark:to-amber-900/20 blur-[100px]"></div>
                    <div className="absolute top-[20%] -right-[10%] w-[60%] h-[60%] rounded-full bg-gradient-to-l from-orange-300/30 to-red-300/30 dark:from-orange-800/20 dark:to-red-900/20 blur-[100px]"></div>
                    {/* Dot Pattern Overlay */}
                    <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(#fb923c 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>
                </div>

                {/* Decorative Elements - Floating Icons */}
                <div className="absolute inset-0 z-0 pointer-events-none hidden md:block select-none overflow-hidden">
                    {/* Pizza - Top Left */}
                    <div className="absolute top-[15%] left-[10%] animate-float opacity-20 dark:opacity-10 text-orange-600 dark:text-orange-400 rotate-[-15deg]">
                        <i className="fa-solid fa-pizza-slice text-8xl filter drop-shadow-lg"></i>
                    </div>

                    {/* Burger - Top Right */}
                    <div className="absolute top-[20%] right-[12%] animate-float-delayed opacity-20 dark:opacity-10 text-amber-600 dark:text-amber-400 rotate-[15deg]">
                        <i className="fa-solid fa-burger text-9xl filter drop-shadow-lg"></i>
                    </div>

                    {/* Ice Cream - Bottom Left */}
                    <div className="absolute bottom-[20%] left-[15%] animate-spin-slow opacity-15 dark:opacity-5 text-pink-500 dark:text-pink-400">
                        <i className="fa-solid fa-ice-cream text-7xl filter drop-shadow-md"></i>
                    </div>

                    {/* Utensils - Bottom Right */}
                    <div className="absolute bottom-[25%] right-[20%] animate-float opacity-15 dark:opacity-5 text-gray-600 dark:text-gray-400 rotate-[10deg]">
                        <i className="fa-solid fa-utensils text-6xl filter drop-shadow-md"></i>
                    </div>
                </div>

                <div className="relative z-10 max-w-4xl mx-auto text-center">
                    {/* Location Badge */}
                    <div className="inline-flex items-center gap-2 bg-white/50 dark:bg-gray-800/50 backdrop-blur-md border border-gray-200 dark:border-gray-700 rounded-full px-5 py-2 mb-8 shadow-sm">
                        <i className="fa-solid fa-location-dot text-orange-500"></i>
                        <span className="text-gray-700 dark:text-gray-200 font-medium tracking-wide text-sm">{cityName}</span>
                    </div>

                    <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 dark:text-white mb-6 leading-tight tracking-tight">
                        Order food from <br className="hidden md:block" />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500">
                            favourite restaurants
                        </span>
                    </h1>
                    <p className="text-gray-600 dark:text-gray-400 text-lg md:text-xl mb-10 max-w-2xl mx-auto">
                        Discover the best food & drinks in {cityName}. Fast delivery to your doorstep.
                    </p>

                    {/* Search Box */}
                    <div className="flex items-center w-full max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-full shadow-xl shadow-orange-500/10 dark:shadow-none border border-gray-200 dark:border-gray-700 p-2 transition-all focus-within:ring-2 focus-within:ring-orange-500/50 focus-within:border-orange-500">
                        <div className="pl-4 pr-3 text-gray-400 dark:text-gray-500">
                            <i className="fa-solid fa-magnifying-glass text-lg"></i>
                        </div>
                        <input
                            type="text"
                            placeholder="Search for restaurants or cuisines..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onKeyPress={handleKeyPress}
                            className="flex-1 bg-transparent text-gray-700 dark:text-gray-200 placeholder-gray-400 focus:placeholder-gray-300 outline-none text-base md:text-lg w-full"
                        />
                        <button
                            onClick={() => filterRestaurants(searchText, allRestaurants)}
                            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-full px-8 py-3 md:py-4 transition-all transform active:scale-95 shadow-lg shadow-orange-500/25 flex-shrink-0 whitespace-nowrap ml-2"
                        >
                            Search
                        </button>
                    </div>
                </div>
            </section>

            {/* Food Categories */}
            {foodCategories && foodCategories.length > 0 && (
                <section className="max-w-[1400px] mx-auto px-6 py-8">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">What's on your mind?</h2>
                        {selectedCategory && (
                            <button
                                onClick={() => {
                                    setSelectedCategory(null);
                                    setSearchResults(null);
                                    setErrorMessage("");
                                }}
                                className="flex items-center gap-2 text-sm text-orange-500 hover:text-orange-600 font-medium"
                            >
                                <i className="fa-solid fa-xmark"></i>
                                Clear filter
                            </button>
                        )}
                    </div>
                    <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
                        {foodCategories.slice(0, 12).map((category: FoodCategory) => {
                            const isSelected = selectedCategory === category.action?.text;
                            return (
                                <div
                                    key={category.id}
                                    onClick={() => handleCategoryClick(category.action?.text)}
                                    className="flex-shrink-0 text-center cursor-pointer group"
                                >
                                    <div className={`w-20 h-20 rounded-full overflow-hidden mb-2 ring-2 transition-all transform group-hover:scale-105 ${isSelected ? 'ring-orange-500 ring-offset-2 scale-105' : 'ring-transparent group-hover:ring-orange-400'
                                        }`}>
                                        <img
                                            src={IMG_CDN_URL + category.imageId}
                                            alt={category.action?.text}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <span className={`text-xs font-medium transition-colors ${isSelected ? 'text-orange-500' : 'text-gray-700 dark:text-gray-300 group-hover:text-orange-500'
                                        }`}>
                                        {category.action?.text}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Top Brands */}
            {topBrands && topBrands.length > 0 && !searchResults && !selectedCategory && (
                <section className="w-full bg-white dark:bg-gray-900 py-10 border-y border-gray-100 dark:border-gray-800 transition-colors">
                    <div className="max-w-[1400px] mx-auto px-6">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                                    Top restaurant chains in {cityName}
                                </h2>
                                <p className="text-gray-500 dark:text-gray-400 text-sm">Handpicked favorites just for you</p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center text-gray-700 dark:text-gray-300 transition-colors border border-gray-200 dark:border-gray-700"
                                    onClick={() => {
                                        const container = document.getElementById('top-brands-scroll');
                                        if (container) container.scrollBy({ left: -320, behavior: 'smooth' });
                                    }}
                                >
                                    <i className="fa-solid fa-chevron-left text-sm"></i>
                                </button>
                                <button
                                    className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center text-gray-700 dark:text-gray-300 transition-colors border border-gray-200 dark:border-gray-700"
                                    onClick={() => {
                                        const container = document.getElementById('top-brands-scroll');
                                        if (container) container.scrollBy({ left: 320, behavior: 'smooth' });
                                    }}
                                >
                                    <i className="fa-solid fa-chevron-right text-sm"></i>
                                </button>
                            </div>
                        </div>

                        <div id="top-brands-scroll" className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide scroll-smooth">
                            {topBrands.map((restaurant: RestaurantCardType, index: number) => (
                                <Link
                                    to={`/restaurant/${restaurant?.info?.id}`}
                                    key={restaurant?.info?.id}
                                    className="flex-shrink-0 w-[calc(25%-12px)] min-w-[280px]"
                                >
                                    <RestaurantCard restaurant={restaurant.info} />
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Error Message */}
            {errorMessage && (
                <div className="text-center py-8 text-red-500 font-medium">
                    {errorMessage}
                </div>
            )}

            {/* Restaurant Grid */}
            <section className="max-w-[1400px] mx-auto px-6 py-12">
                <div className="flex items-center justify-between mb-8">
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
                        {searchResults ? "Search Results" : `Restaurants with online food delivery in ${cityName}`}
                    </h2>
                    <span className="text-gray-500 dark:text-gray-400">
                        {totalItems} places
                    </span>
                </div>

                {totalItems === 0 ? (
                    <div className="text-center py-16">
                        <div className="text-6xl mb-4">🍽️</div>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                            No restaurants found
                        </h2>
                        <p className="text-gray-500 dark:text-gray-400">Try a different search term</p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {currentRestaurants.map((restaurant: RestaurantCardType) => (
                                <Link
                                    to={`/restaurant/${restaurant?.info?.id}`}
                                    key={restaurant?.info?.id}
                                    className="block"
                                >
                                    <RestaurantCard restaurant={restaurant.info} />
                                </Link>
                            ))}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-center gap-2 mt-12">
                                <button
                                    onClick={() => goToPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${currentPage === 1
                                        ? "text-gray-400 cursor-not-allowed dark:text-gray-600"
                                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                                        }`}
                                >
                                    <i className="fa-solid fa-chevron-left text-sm"></i>
                                    <span className="hidden sm:inline">Previous</span>
                                </button>

                                <div className="flex items-center gap-1">
                                    {getPageNumbers().map((page, index) => (
                                        <button
                                            key={index}
                                            onClick={() => typeof page === 'number' && goToPage(page)}
                                            disabled={page === '...'}
                                            className={`w-10 h-10 rounded-lg font-medium transition-colors ${page === currentPage
                                                ? "bg-orange-500 text-white"
                                                : page === '...'
                                                    ? "text-gray-400 cursor-default"
                                                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                                                }`}
                                        >
                                            {page}
                                        </button>
                                    ))}
                                </div>

                                <button
                                    onClick={() => goToPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${currentPage === totalPages
                                        ? "text-gray-400 cursor-not-allowed dark:text-gray-600"
                                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                                        }`}
                                >
                                    <span className="hidden sm:inline">Next</span>
                                    <i className="fa-solid fa-chevron-right text-sm"></i>
                                </button>
                            </div>
                        )}

                        {totalPages > 1 && (
                            <p className="text-center text-gray-500 dark:text-gray-400 text-sm mt-4">
                                Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} restaurants
                            </p>
                        )}
                    </>
                )}
            </section>
        </main >
    );
};

export default Body;
